import { createContext } from 'react';
import { APIError } from '../../services/api';
import { TChannel } from '../operator/operator-context';
import type { Time } from "../../types/time";

/**************************
 *
 * Sites
 *
 **************************/

export interface Sites {
    data: TSite[];
    errors: APIError;
    event_id: number;
    flow: string;
}

export interface TSite {
    uid: string;
    handle: string;
    name: string;
    lat: number;
    lng: number;
    timezone: string;
    email: string;
    phone: string;
    address_1: string;
    address_2: null;
    address_city: string;
    address_postcode: string;
    address_country: string;
    channels: Channels;
    is_open: boolean;
    opening_times: string;
    opening_hours: OpeningHours[];
    sites_count: number;
    // catering sites that let customers share a basket
    group_ordering?: boolean;
    distance?: number;
}

export interface Channels {
    collection?: Collection;
    dine_in?: DineIn;
    delivery?: Delivery;
    catering?: Catering;
    venues?: Venues;
}

export interface Collection {
    is_open: boolean;
    opening_times?: string;
    opening_hours: OpeningHours[];
}

export interface Delivery {
    is_open: boolean;
    opening_hours: OpeningHours[];
    delivery_time_summary: string;
    delivery_fee_summary: string;
}

export interface Catering {
    is_open: boolean;
    opening_hours: OpeningHours[];
    delivery_time_summary: string;
    delivery_fee_summary: string;
}

export interface Venues {
    is_open: boolean;
    opening_hours: OpeningHours[];
    delivery_time_summary: string;
    delivery_fee_summary: string;
}

export interface DineIn {
    is_open: boolean;
    opening_hours: OpeningHours[];
    opening_times: string;
    table_numbers: string[];
}

export interface OpeningHours {
    day: string;
    time: string;
}

/**************************
 *
 * Menu
 *
 **************************/

export interface Menu {
    data: MenuData;
    errors: APIError;
    event_id: number;
    flow: string;
}

export interface MenuData {
    site: TSite;
    timeslots: Timeslot[];
    menu: TMenu;
    offers?: Offer[];
    flow?: string;
}

/**************************
 *
 * Offers: what customers get without entering a code. All amounts are in pence.
 *
 **************************/

export type OfferKind = 'item_discount' | 'free_item' | 'buy_x_get_y' | 'spend_and_save';

export interface OfferTimes {
    [day: string]: { start: number; end: number }[]; // mon..sun; 1200 is 12:00 and 2630 is 02:30 the morning after
}

export interface Offer {
    uid: string;
    kind: OfferKind;
    text: string | null; // words of the operator's own, in place of what we say of the offer
    reward_item_uids: string[]; // the free item (one for each menu it is on)
    trigger_item_uids: string[]; // the item customers buy to get a free item (buy X, get Y)
    trigger: 'spend' | 'item' | null; // what unlocks a free item: the order value, or another item
    scope_all: boolean | null; // an item discount is for every item
    scope_category_uids: string[]; // ...or for the items in these categories
    scope_item_uids: string[]; // ...and these items
    scope_names: string[]; // the names of those categories and items, as customers read them
    units: number | null; // how many get the offer, in one order
    free_modifiers: boolean | null; // for a free item: whether what is added to it is free too (null for the other offers)
    discount_type: 'percentage' | 'value' | null;
    percent: number | null;
    value: number | null;
    applies_to: 'subtotal' | 'total' | null;
    min_order: number | null;
    max_order: number | null;
    starts_on: string | null;
    ends_on: string | null;
    times: OfferTimes | null;
}

export interface TMenu {
    uid: string;
    name: string;
    categories: Category[];
    upsells: Item[];
}

export interface Category {
    uid: string;
    name: string;
    description: string;
    is_hidden: boolean;
    schedule: CategorySchedule;
    items: Item[];
}

export interface CategorySchedule {
    mon_start_at?: Time;
    mon_end_at?: Time;
    tue_start_at?: Time;
    tue_end_at?: Time;
    wed_start_at?: Time;
    wed_end_at?: Time;
    thu_start_at?: Time;
    thu_end_at?: Time;
    fri_start_at?: Time;
    fri_end_at?: Time;
    sat_start_at?: Time;
    sat_end_at?: Time;
    sun_start_at?: Time;
    sun_end_at?: Time;
}

export interface Item {
    uid: string;
    name: string;
    description: string;
    price: number;
    is_hidden: boolean;
    is_new: boolean;
    is_special: boolean;
    is_popular: boolean;
    is_offer: boolean;
    is_vegetarian: boolean;
    is_vegan: boolean;
    is_gluten_free: boolean;
    is_halal: boolean;
    is_kosher: boolean;
    allergens: string;
    allergens_list: Array<any>|null;
    image: string;
    modifier_groups: ModifierGroup[];
    quantity?: number;
    modifiers?: ModifierShort[];
    selected_modifiers?: ModifierShort[];
    unselected_default_modifiers?: ModifierShort[];
    modifiersTotal: number;
    item_total: number;
    calories?: string;
    basketIndex?: string;
    // set on lines in a group order
    groupLine?: GroupLineInfo;
}

export interface GroupLineInfo {
    // the guest who added it (the host's own lines have the host's name)
    participant: string | null;
    // mine to change, or the lead's to change on behalf of a guest
    mine: boolean;
    // the name of whatever is no longer orderable
    problem: string | null;
}

export interface ModifierGroup {
    uid: string;
    name: string;
    subtitle: string;
    min: number;
    max: number;
    max_per_item: number|null;
    hidden: boolean;

    multi_choice: boolean;
    default_subtitle: boolean;

    modifiers: Modifier[];
}

export interface Modifier {
    uid: string;
    name: string;
    description: string;
    max: number|null;
    price: number;
    is_hidden: boolean;
    is_default: boolean;
    is_new: boolean;
    is_special: boolean;
    is_popular: boolean;
    is_offer: boolean;
    is_vegetarian: boolean;
    is_vegan: boolean;
    is_gluten_free: boolean;
    is_halal: boolean;
    is_kosher: boolean;
    quantity: number;
    group_uid?: string;
    allergens: string;
    calories?: string;
}

export interface ModifierShort {
    uid: string;
    name: string;
    quantity: number;
    group_uid: string;
    display?: boolean;
}

export interface TSite {
    min_order?: number;
    max_order?: number;
    banner?: string;
    instructions?: null;
    show_instructions_at_checkout?: boolean;
    show_instructions_at_confirm?: boolean;
}

export interface Timeslot {
    is_asap: boolean;
    id: string;
    name: string;
}

export type SiteContextData = {
    site: TSite | null;
    fetchSite: (
        id: string,
        channel: TChannel,
        flow?: string,
        handle?: string,
        tableNumber?: string,
        postcode?: string,
        inGroupOrder?: boolean,
    ) => void;

    sites: TSite[] | null;
    fetchSites: (handle: string, flow: string, postcode?: string) => void;

    channel: TChannel;
    setChannel: (channel: TChannel) => void;

    menu: TMenu | null;
    offers: Offer[];
    timeslots: Timeslot[] | null;
    setTimeslots: (timeslots: Timeslot[]) => void;
    fetchTimeslots: (siteHandle: string, flow: string) => void;

    loading: boolean;
    setLoading: (state: boolean) => void;

    flow: string;
    setFlow: (flow: string) => void;
};

export const SiteContext = createContext<SiteContextData>({
    site: null,
    fetchSite: () => {},

    sites: null,
    fetchSites: () => {},

    menu: null,
    offers: [],
    timeslots: null,
    setTimeslots: () => {},
    fetchTimeslots: () => {},

    channel: 'collection',
    setChannel: () => {},

    loading: false,
    setLoading: () => {},

    flow: 'noflow',
    setFlow: () => {},
});
